// RFC 6238 TOTP — browser counterpart of functions/gate/totp.js.
// SHA-1, 6 digits, 30s step. Must stay byte-identical to the server's
// generator or every code it produces will be rejected.
//
// Uses Web Crypto rather than a dependency: this is ~30 lines, and a TOTP
// library is a poor thing to take an unaudited transitive tree for.
const B32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

export function base32Decode(str) {
  const clean = String(str).toUpperCase().replace(/[^A-Z2-7]/g, '');
  let bits = 0, value = 0;
  const out = [];
  for (const ch of clean) {
    value = (value << 5) | B32_ALPHABET.indexOf(ch); bits += 5;
    if (bits >= 8) { out.push((value >>> (bits - 8)) & 255); bits -= 8; }
  }
  return new Uint8Array(out);
}

/** HOTP for a given counter. Mirrors the server's dynamic-truncation step. */
async function hotp(secretBytes, counter) {
  const msg = new Uint8Array(8);
  // Counter is a 64-bit big-endian int; JS bitwise ops are 32-bit, so fill
  // the low four bytes and shift the high half separately.
  const view = new DataView(msg.buffer);
  view.setUint32(0, Math.floor(counter / 2 ** 32));
  view.setUint32(4, counter >>> 0);
  const key = await crypto.subtle.importKey(
    'raw', secretBytes, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign'],
  );
  const sig = new Uint8Array(await crypto.subtle.sign('HMAC', key, msg));
  const offset = sig[sig.length - 1] & 0x0f;
  const code = ((sig[offset] & 0x7f) << 24) | (sig[offset + 1] << 16)
    | (sig[offset + 2] << 8) | sig[offset + 3];
  return String(code % 1000000).padStart(6, '0');
}

/** Current 6-digit code for a base32 secret. */
export async function generateCode(secret, { stepSeconds = 30, now = Date.now() } = {}) {
  const bytes = base32Decode(secret);
  if (!bytes.length) throw new Error('Invalid gate secret');
  return hotp(bytes, Math.floor(now / 1000 / stepSeconds));
}

/** Seconds until the current code rolls over — for a countdown. */
export function secondsRemaining({ stepSeconds = 30, now = Date.now() } = {}) {
  return stepSeconds - Math.floor(now / 1000) % stepSeconds;
}
